import React from "react";
import { motion } from "framer-motion";
import { Activity } from "lucide-react";

// Animated left-to-right flow of movements, each with a benefit badge.
export function MovementFlow({ steps, accent, testid = "movement-flow" }) {
  return (
    <div className="relative" data-testid={testid}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4 relative">
        {steps.map((s, i) => (
          <motion.div
            key={s.move}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5, delay: i * 0.15 }}
            className="relative flex flex-col items-center text-center px-4"
            data-testid={`${testid}-step-${i}`}
          >
            {i < steps.length - 1 && (
              <span className="hidden md:block absolute top-9 left-1/2 w-full h-[2px] flow-line" style={{ color: `${accent}66` }} />
            )}
            {s.clip ? (
              <div className="relative z-10 h-24 w-24 rounded-full overflow-hidden mb-5" style={{ border: `2px solid ${accent}`, boxShadow: `0 0 26px ${accent}55` }}>
                <video className="h-full w-full object-cover" autoPlay muted loop playsInline preload="auto">
                  <source src={s.clip} type="video/mp4" />
                  <source src={s.clip.replace(".mp4", ".webm")} type="video/webm" />
                </video>
              </div>
            ) : (
              <div
                className="beat-pulse relative z-10 grid place-items-center h-[72px] w-[72px] rounded-full mb-5"
                style={{ backgroundColor: `${accent}22`, border: `2px solid ${accent}`, color: accent, "--pulse-color": `${accent}55`, animationDelay: `${i * 0.3}s` }}
              >
                <Activity size={28} />
              </div>
            )}
            <h4 className="font-serif text-xl md:text-2xl">{s.move}</h4>
            <span className="mt-3 inline-block font-mono text-[10px] tracking-[0.15em] uppercase px-3 py-1.5 rounded-full" style={{ backgroundColor: `${accent}1A`, color: accent, border: `1px solid ${accent}44` }}>{s.badge}</span>
            <p className="mt-3 text-fg/65 text-[15px] leading-snug">{s.benefit}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// Animated horizontal bars that fill on scroll (illustrative program emphasis).
export function OutcomeBars({ items, accent, testid = "outcome-bars" }) {
  return (
    <div className="space-y-5" data-testid={testid}>
      {items.map((it, i) => (
        <div key={it.label} data-testid={`${testid}-item-${i}`}>
          <div className="flex justify-between items-baseline mb-2">
            <span className="font-medium">{it.label}</span>
            <span className="font-mono text-[11px] tracking-[0.1em]" style={{ color: accent }}>{it.pct}%</span>
          </div>
          <div className="h-2.5 rounded-full overflow-hidden bg-white/10">
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: `${it.pct}%` }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 1.1, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="h-full rounded-full"
              style={{ background: `linear-gradient(90deg, ${accent}, ${accent}aa)`, boxShadow: `0 0 16px ${accent}55` }}
            />
          </div>
          {it.note && <p className="mt-1.5 text-fg/55 text-sm leading-snug">{it.note}</p>}
        </div>
      ))}
    </div>
  );
}

// Animated circular progress rings for impact areas.
export function ImpactRings({ items, accent, testid = "impact-rings" }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-8" data-testid={testid}>
      {items.map((it, i) => (
        <motion.div
          key={it.label}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5, delay: i * 0.15 }}
          className="glass shape-r p-7 text-center"
          data-testid={`${testid}-item-${i}`}
        >
          <div className="relative grid place-items-center h-24 w-24 mx-auto mb-5">
            <svg viewBox="0 0 36 36" className="h-24 w-24 -rotate-90">
              <circle cx="18" cy="18" r="16" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="2.5" />
              <motion.circle
                cx="18" cy="18" r="16" fill="none" stroke={accent} strokeWidth="2.5" strokeLinecap="round"
                initial={{ strokeDasharray: "0 100.5" }}
                whileInView={{ strokeDasharray: `${it.level} 100.5` }}
                viewport={{ once: true, amount: 0.6 }}
                transition={{ duration: 1.3, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                style={{ filter: `drop-shadow(0 0 6px ${accent}88)` }}
              />
            </svg>
            <span className="absolute font-serif text-lg" style={{ color: accent }}>{it.icon}</span>
          </div>
          <h4 className="font-serif text-xl">{it.label}</h4>
          <p className="mt-2 text-fg/65 text-[15px] leading-snug">{it.desc}</p>
        </motion.div>
      ))}
    </div>
  );
}

// Animated energy arc (SVG) with a dot travelling along the curve + stage labels.
export function EnergyArc({ stages, accent, testid = "energy-arc" }) {
  const path = "M 20 180 C 140 180, 180 60, 320 60 C 460 60, 500 20, 620 40 C 720 55, 760 130, 880 150";
  return (
    <div className="glass shape-r p-6 md:p-10 overflow-hidden" data-testid={testid}>
      <svg viewBox="0 0 900 220" className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="arcGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={`${accent}00`} />
            <stop offset="20%" stopColor={accent} />
            <stop offset="100%" stopColor={accent} />
          </linearGradient>
        </defs>
        <motion.path
          d={path} fill="none" stroke="url(#arcGrad)" strokeWidth="3" strokeLinecap="round"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }}
          transition={{ duration: 2, ease: "easeInOut" }}
        />
        <circle r="7" fill={accent} style={{ filter: `drop-shadow(0 0 8px ${accent})` }}>
          <animateMotion dur="4s" repeatCount="indefinite" path={path} />
        </circle>
        {[[20, 180], [320, 60], [620, 40], [880, 150]].map((p, i) => (
          <circle key={i} cx={p[0]} cy={p[1]} r="5" fill="var(--bg)" stroke={accent} strokeWidth="2.5" />
        ))}
      </svg>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
        {stages.map((s, i) => (
          <div key={s} className="text-center" data-testid={`${testid}-stage-${i}`}>
            <span className="font-mono text-[10px] tracking-[0.15em] uppercase" style={{ color: accent }}>Stage {i + 1}</span>
            <p className="font-serif text-base md:text-lg leading-snug mt-1">{s}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
